import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Slot } from 'radix-ui';
import { cn } from '@/lib/cn';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'quiet' | 'accent';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** Phosphor icon element rendered before the label. */
  icon?: ReactNode;
  iconRight?: ReactNode;
  /** Render as the child element (e.g. a router <Link>). */
  asChild?: boolean;
  block?: boolean;
}

/**
 * A3 button rule (ARCHITECTURE §11):
 * - `primary`: the page's main action. Brand blue with white text on light surfaces; inside
 *   `surface-field` (sidebar, highlight cards, auth panel) the same variant turns into the saffron
 *   CTA with dark ink text, because the field re-points the primary tokens.
 * - `accent`: the saffron CTA on light surfaces, for the one action that matters most on a screen
 *   (upgrade to Pro, send the review request). At most one per screen.
 * - `secondary`: outlined (1.5px inset line, white fill), for everything next to a primary.
 * - `ghost` / `quiet`: toolbars and inline actions. `danger`: destructive, AA red fill.
 */
const base =
  'relative inline-flex select-none items-center justify-center gap-2 whitespace-nowrap font-bold tracking-[-0.005em] ' +
  'transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-out ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus ' +
  'disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 ' +
  'active:translate-y-px [&_svg]:shrink-0';

const variants: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-on-primary hover:bg-primary-hover active:bg-primary-strong ' +
    'shadow-[0_1px_2px_rgb(4_18_34/0.18)]',
  accent:
    'bg-accent text-on-accent hover:bg-accent-strong active:bg-accent-strong ' +
    'shadow-[0_1px_2px_rgb(4_18_34/0.18)]',
  secondary:
    'bg-surface text-ink shadow-[inset_0_0_0_1.5px_var(--gh-hairline-strong)] ' +
    'hover:bg-canvas hover:shadow-[inset_0_0_0_1.5px_var(--gh-control-border)]',
  ghost: 'text-ink-soft hover:bg-sunken hover:text-ink',
  quiet: 'text-primary-ink hover:bg-primary-soft underline-offset-4',
  danger:
    // danger-fill (not the lighter `negative`) keeps white text >=4.5:1 in both themes.
    'bg-danger-fill text-white hover:bg-danger-fill-hover active:bg-danger-fill-hover ' +
    'shadow-[0_1px_2px_rgb(4_18_34/0.18)]',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-8 rounded-sm px-3 text-sm [&_svg]:size-4',
  md: 'h-9.5 rounded-sm px-4 text-[0.875rem] [&_svg]:size-[18px]',
  lg: 'h-11 rounded-md px-5 text-base [&_svg]:size-5',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading,
    icon,
    iconRight,
    asChild,
    block,
    className,
    children,
    disabled,
    type,
    ...rest
  },
  ref,
) {
  const classes = cn(base, variants[variant], sizes[size], block && 'w-full', className);
  if (asChild) {
    return (
      // Slottable keeps icon/iconRight: they are merged into the child element around its own
      // children (e.g. <Button asChild icon={…}><Link>Label</Link></Button>).
      <Slot.Root ref={ref} className={classes} {...rest}>
        {icon}
        <Slot.Slottable>{children}</Slot.Slottable>
        {iconRight}
      </Slot.Root>
    );
  }
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner className={size === 'lg' ? 'size-5' : 'size-4'} /> : icon}
      <span className={cn(loading && 'opacity-90')}>{children}</span>
      {!loading && iconRight}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonProps, 'icon' | 'iconRight' | 'children'> {
  /** Required accessible label (also used as tooltip text by callers). */
  label: string;
  children: ReactNode;
}

const iconSizes: Record<ButtonSize, string> = {
  sm: 'size-8 rounded-sm [&_svg]:size-4',
  md: 'size-9.5 rounded-sm [&_svg]:size-[18px]',
  lg: 'size-11 rounded-md [&_svg]:size-5',
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, variant = 'ghost', size = 'md', className, children, loading, disabled, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-label={label}
      title={label}
      disabled={disabled || loading}
      className={cn(base, variants[variant], iconSizes[size], 'px-0', className)}
      {...rest}
    >
      {loading ? <Spinner /> : children}
    </button>
  );
});
